import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';

import type { TabOnClickEvent } from '@simplywallst/ui-core';
import { Box, Tab, TabList } from '@simplywallst/ui-core';

import {
  MonitoringPanel,
  MonitoringPanelTabList,
  MonitoringPanelTabPanel,
} from '@/components/MonitoringPanel';

import type { Holding } from '@/hooks/portfolio/usePortfolio';

import { useSortedHoldings } from '../../hooks/useHoldingsSorting';
import { ScrollY } from './styled';

const Overview = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Overview,
});

const Updates = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Updates,
});

const HoldingPanel = loadable(() => import('./HoldingPanel'), {
  resolveComponent: (c) => c.HoldingPanel,
});

const Notes = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Notes,
});

const BlockedHoldingsPanel = loadable(() => import('./BlockedHoldingsPanel'), {
  resolveComponent: (c) => c.BlockedHoldingsPanel,
});

type PortfolioPanelTab = 'overview' | 'holding' | 'updates' | 'notes';

export interface Props {
  holdingId: Holding['id'] | null;
  setHoldingId: (id: Holding['id'] | null) => void;
  holdings: Holding[];
  excludedHoldings: Holding[];
}

export const PortfolioPanel = ({
  holdingId,
  setHoldingId,
  excludedHoldings,
  holdings,
}: Props) => {
  const sortedHoldings = useSortedHoldings(holdings);

  const [selectedTab, selectTab] = useState<PortfolioPanelTab>('overview');
  const { Track, trackEvent } = useTracking({
    modifier: 'sidePanel',
  });

  const selectedHolding = useMemo(() => {
    return (
      [...excludedHoldings, ...sortedHoldings]?.find(
        (holding) => holding.id === holdingId
      ) ?? null
    );
  }, [excludedHoldings, sortedHoldings, holdingId]);

  const selectedCompany = selectedHolding?.company ?? null;

  // When the currently selected item is removed (if it doesn't exist, close the panel)
  useEffect(() => {
    if (selectedHolding === null) setHoldingId(null);
  }, [selectedHolding, setHoldingId]);

  const handleTabClick = useCallback(
    (event: TabOnClickEvent<PortfolioPanelTab>) => {
      const { value } = event.target;
      selectTab(value);
      trackEvent({ action: 'tabClick', value });
    },
    [trackEvent]
  );

  const handleClose = useCallback(() => {
    setHoldingId(null);
    trackEvent({
      action: 'click',
    });
  }, [setHoldingId, trackEvent]);

  if (selectedHolding?.isAnalysisRestricted) {
    return (
      <BlockedHoldingsPanel
        selectedCompany={selectedCompany}
        onClose={handleClose}
      />
    );
  }

  return (
    <Track>
      <MonitoringPanel company={selectedCompany} onClose={handleClose}>
        <ScrollY data-cy-id="portfolio-panel">
          <Box marginX="x2" marginBottom="x1">
            <MonitoringPanelTabList>
              <TabList
                templateRows="min-content"
                templateColumns={`repeat(${4}, max-content)`}
              >
                <Tab<PortfolioPanelTab>
                  value="overview"
                  selected={selectedTab === 'overview'}
                  onClick={handleTabClick}
                  styleType="subnav"
                >
                  Overview
                </Tab>
                <Tab<PortfolioPanelTab>
                  value="holding"
                  selected={selectedTab === 'holding'}
                  onClick={handleTabClick}
                  styleType="subnav"
                >
                  Holding
                </Tab>
                <Tab<PortfolioPanelTab>
                  value="updates"
                  selected={selectedTab === 'updates'}
                  onClick={handleTabClick}
                  styleType="subnav"
                >
                  Updates
                </Tab>
                <Tab<PortfolioPanelTab>
                  value="notes"
                  selected={selectedTab === 'notes'}
                  onClick={handleTabClick}
                  styleType="subnav"
                >
                  Notes
                </Tab>
              </TabList>
            </MonitoringPanelTabList>
          </Box>
          <MonitoringPanelTabPanel
            value="overview"
            selected={selectedTab === 'overview'}
          >
            {selectedCompany ? (
              <Overview company={selectedCompany} selectTab={selectTab} />
            ) : null}
          </MonitoringPanelTabPanel>
          <MonitoringPanelTabPanel
            value="holding"
            selected={selectedTab === 'holding'}
          >
            {selectedHolding ? (
              <HoldingPanel holding={selectedHolding} />
            ) : null}
          </MonitoringPanelTabPanel>
          <MonitoringPanelTabPanel
            value="updates"
            selected={selectedTab === 'updates'}
            paddingX="x1spacePx"
          >
            {selectedCompany ? (
              <Updates canonicalURL={selectedCompany.canonicalURL} />
            ) : null}
          </MonitoringPanelTabPanel>
          <MonitoringPanelTabPanel
            value="updates"
            selected={selectedTab === 'notes'}
            paddingX="x1spacePx"
            paddingTop="x1spacePx"
            paddingBottom="x12spacePx"
          >
            <Notes
              uniqueSymbol={selectedCompany?.tickerSymbol}
              companyId={selectedCompany?.id}
              isFloatingButton
            />
          </MonitoringPanelTabPanel>
        </ScrollY>
      </MonitoringPanel>
    </Track>
  );
};
